import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from '@/hooks/useTranslation';
import { latestPriceDropPct, type PricePoint } from '@/lib/priceHistory';

const fmt = (n: number, cur: string) =>
  `${Math.round(n).toLocaleString('ro-RO')} ${cur === 'EUR' ? '€' : cur}`;

export const PriceHistoryCard: React.FC<{ listingId: string }> = ({ listingId }) => {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['public-price-history', listingId],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_public_listing_price_history', { _listing_id: listingId });
      if (error) return [] as PricePoint[];
      return (data ?? []) as PricePoint[];
    },
    staleTime: 5 * 60_000,
  });
  const points = data ?? [];
  const pct = latestPriceDropPct(points);
  if (pct == null) return null;
  const date = new Date(points[0].changed_at).toLocaleDateString('ro-RO');

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 mb-2">
        <TrendingDown className="h-4 w-4 text-primary" aria-hidden />
        <h4 className="font-medium text-foreground text-sm">{t('price_history_card.title')}</h4>
      </div>
      <p className="text-sm text-foreground mb-2">
        {t('price_history_card.drop').replace('{pct}', String(pct)).replace('{date}', date)}
      </p>
      <ul className="space-y-1 text-sm">
        {points.slice(0, 5).map((pt, i) => {
          const cur = (pt.currency || 'EUR').toUpperCase();
          return (
            <li key={i} className="flex justify-between text-muted-foreground">
              <span>{new Date(pt.changed_at).toLocaleDateString('ro-RO')}</span>
              <span className={i === 0 ? 'text-foreground font-medium' : 'line-through'}>
                {fmt(pt.price, cur)}
                {cur !== 'EUR' && pt.price_eur != null ? ` (~${fmt(pt.price_eur, 'EUR')})` : ''}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-muted-foreground mt-2">{t('price_history_card.note')}</p>
    </div>
  );
};
